/**
 * HeroLoop — Prevail marketing reel
 *
 * A self-contained cinematic loop showcasing the Prevail intelligence layer.
 * Lives entirely in src/components/marketing/HeroLoop/. Imports nothing from
 * the working demo. Deleting this folder fully reverts.
 *
 * Beats (full variant, ~13s):
 *   1. Drop a complaint
 *   2. The system reads it
 *   3. Strategy check
 *   4. Research design
 *   5. The panel deliberates
 *   6. Insight resolves
 *
 * Short variant (~7s) cuts beats 3 and 4.
 *
 * Liability discipline: no dollar figures, no juror percentages, no real
 * case content, no predictive language. Stylized abstractions only.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  FileText, Check, ClipboardList, MessageSquare, Scale, Users, RotateCcw,
  Target, Sparkles, TrendingUp, Quote, Lock,
} from 'lucide-react';

// ── Timing & easing ─────────────────────────────────────────────────────────

const EASE = [0.22, 1, 0.36, 1] as const; // Expo-out: heavy door closing on a luxury car
const EASE_IN = [0.32, 0, 0.67, 0] as const; // For exits

interface BeatTick {
  id: number;
  duration: number;
}

// Timing principle: each beat should finish its internal motion at roughly
// 55-65% of its duration, then HOLD on the final state so the viewer's eye
// can rest and read. Total full loop ≈ 32s.
const TIMELINE_FULL: BeatTick[] = [
  { id: 1, duration: 2800 },  // Drop — pleading lands, brief recognition
  { id: 2, duration: 4200 },  // Bundle intake — multiple files cascade in
  { id: 3, duration: 4800 },  // Read + Recommend — densest beat
  { id: 4, duration: 3800 },  // Design — 5 phase rows + dot indicators
  { id: 5, duration: 4200 },  // Deliberates — wave, then color split, hold
  { id: 6, duration: 3200 },  // Resolves — findings card with glow
  { id: 7, duration: 4800 },  // Ask anything — single example interaction
  { id: 8, duration: 5400 },  // CRESCENDO — breadth of capabilities
  { id: 9, duration: 8500 },  // CLOSING — logo + value props, longer hold for impact
];

// Short variant tells the same story in roughly half the time. Keeps the
// four narrative anchors: input (1), understanding (3), deliberation (5),
// the breadth crescendo (8), and the brand close (9).
const TIMELINE_SHORT: BeatTick[] = [
  { id: 1, duration: 2200 },
  { id: 3, duration: 3800 },
  { id: 5, duration: 3400 },
  { id: 8, duration: 4800 },
  { id: 9, duration: 8000 },
];

// ── Tokens ──────────────────────────────────────────────────────────────────

// Card chrome: layered shadows for photographic depth.
//   1. Far drop shadow (60px blur, dark) — the "card sits in space" base layer
//   2. Mid drop shadow (35px blur) — softens the transition from card to background
//   3. Subtle 1px outer ring at low white opacity — defines the edge against the
//      dark background without a hard border (replaces the previous border-slate-200)
//   4. Inset 1px bottom shadow (very subtle) — implies "the card has thickness"
// This is the same shadow stack used by Apple, Linear, and Stripe for cards
// on dark surfaces. The eye doesn't consciously see it but the depth is felt.
const cardClass = 'bg-white rounded-2xl shadow-[0_30px_60px_-15px_rgba(0,0,0,0.5),0_15px_35px_-8px_rgba(0,0,0,0.25),0_0_0_1px_rgba(255,255,255,0.06),inset_0_-1px_0_rgba(15,23,42,0.06)]';

const labelClass = 'text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500';
const headerClass = 'text-sm font-bold text-slate-900';

// Inline font rendering hints for crisp commercial-grade text.
// Applied at the root container so every text node inherits.
const FONT_RENDERING: React.CSSProperties = {
  fontFeatureSettings: '"kern", "liga", "calt", "ss01"',
  textRendering: 'optimizeLegibility',
  WebkitFontSmoothing: 'antialiased',
  MozOsxFontSmoothing: 'grayscale',
  letterSpacing: '-0.011em',
};

// JuryView brand: navy + gold against dark slate stage.
// Gold (--gold-500: #d4a84b) is the cinematic accent — warm against dark slate.
// Navy (--navy-900: #0d2840) is the brand anchor (also used for the deep tone).
const BRAND = {
  primary: '#d4a84b',       // gold-500 — primary accent
  primarySoft: '#e8c06a',   // gold-300 — softer highlight
  primaryFaint: '#fdf6e7',  // very pale gold for backgrounds
  primaryDeep: '#8a6a1f',   // dark gold for text on faint bg
  emerald: '#10b981',
  slate950: '#020617',
  slate900: '#0f172a',
  slate800: '#1e293b',
  slate700: '#334155',
  slate500: '#64748b',
  slate400: '#94a3b8',
  slate300: '#cbd5e1',
  slate200: '#e2e8f0',
  slate100: '#f1f5f9',
  white: '#ffffff',
} as const;

// ── Component ───────────────────────────────────────────────────────────────

interface HeroLoopProps {
  variant?: 'full' | 'short';
  /** Optional className for the outer wrapper (e.g. for embedding in a page section) */
  className?: string;
}

export const HeroLoop: React.FC<HeroLoopProps> = ({ variant = 'full', className }) => {
  const timeline = variant === 'short' ? TIMELINE_SHORT : TIMELINE_FULL;
  const [tickIndex, setTickIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isTabHidden, setIsTabHidden] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  // Refs hold the latest values without triggering effect re-runs.
  // This is the bulletproof pattern for animation sequencers — the effect
  // only depends on tickIndex and isPaused, so it can't get cancelled by
  // unrelated state churn (e.g. prefersReducedMotion resolving null → false
  // after mount, or React Strict Mode's double-mount in dev).
  const timelineRef = useRef(timeline);
  const reducedRef = useRef(prefersReducedMotion);
  timelineRef.current = timeline;
  reducedRef.current = prefersReducedMotion;

  // Reset to Beat 1 when variant changes (prevents tickIndex pointing past
  // the end of a shorter timeline if someone swaps full → short at runtime).
  useEffect(() => {
    setTickIndex(0);
  }, [variant]);

  // Page Visibility API — pause the loop when the tab is backgrounded so
  // setTimeout drift doesn't desync the sequencer with the visible state.
  // Without this, switching tabs for 30 seconds and coming back would
  // either leave the loop frozen or jump multiple beats at once.
  useEffect(() => {
    const handleVisibility = () => {
      setIsTabHidden(document.hidden);
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Preload the closing-slide shield icon so Beat 9 doesn't flash on its
  // first appearance. The browser caches it before we ever render it.
  useEffect(() => {
    const img = new Image();
    img.src = '/assets/shield-icon.png';
  }, []);

  const currentBeat = timeline[tickIndex]?.id ?? 1;

  // Sequencer: advance through timeline
  useEffect(() => {
    if (isPaused || isTabHidden) return;
    const tl = timelineRef.current;
    const tick = tl[tickIndex];
    if (!tick) return;
    const reduced = reducedRef.current;
    const duration = reduced ? Math.max(1500, tick.duration * 0.7) : tick.duration;
    const timer = window.setTimeout(() => {
      setTickIndex((i) => (i + 1) % tl.length);
    }, duration);
    return () => window.clearTimeout(timer);
  }, [tickIndex, isPaused, isTabHidden]);

  return (
    <div
      className={`relative w-full min-h-[680px] bg-slate-950 flex items-center justify-center overflow-hidden ${className ?? ''}`}
      style={FONT_RENDERING}
      role="img"
      aria-label="JuryView product walkthrough — case file intake, synthetic juror deliberation, and case insights"
    >
      {/* Ambient background — subtle radial vignette so the card pops */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at center, rgba(68, 167, 222, 0.08) 0%, transparent 60%)`,
        }}
      />

      {/* Stage */}
      <div className="relative w-full max-w-5xl mx-auto px-6 py-8 flex flex-col items-center justify-center">
        {/* BrandMark intentionally removed for the JuryView embed —
            the surrounding section already shows "JuryView in motion"
            on the left text column, so the in-stage brandmark would be
            redundant and cost vertical real estate. */}

        {/* Beat stage — sized for Beat 8 Crescendo (tallest beat ~470px).
            Pause-on-pointer (covers both mouse and touch input). */}
        <div
          className="relative w-full max-w-2xl h-[490px] flex items-center justify-center"
          onPointerEnter={() => setIsPaused(true)}
          onPointerLeave={() => setIsPaused(false)}
        >
          <AnimatePresence mode="wait">
            {currentBeat === 1 && <Beat1Drop key="b1" reduced={!!prefersReducedMotion} />}
            {currentBeat === 2 && <Beat2BundleIntake key="b2" reduced={!!prefersReducedMotion} />}
            {currentBeat === 3 && <Beat3ReadAndRecommend key="b3" reduced={!!prefersReducedMotion} />}
            {currentBeat === 4 && <Beat4Design key="b4" reduced={!!prefersReducedMotion} />}
            {currentBeat === 5 && <Beat5Deliberates key="b5" reduced={!!prefersReducedMotion} />}
            {currentBeat === 6 && <Beat6Resolves key="b6" reduced={!!prefersReducedMotion} />}
            {currentBeat === 7 && <Beat7AskAnything key="b7" reduced={!!prefersReducedMotion} />}
            {currentBeat === 8 && <Beat8Crescendo key="b8" reduced={!!prefersReducedMotion} />}
            {currentBeat === 9 && <Beat9Closing key="b9" reduced={!!prefersReducedMotion} />}
          </AnimatePresence>
        </div>

        {/* Caption — minimal, evolves with the beat */}
        <BeatCaption beatId={currentBeat} />

        {/* Beat indicator dots */}
        <div className="flex items-center gap-2 mt-5">
          {timeline.map((t, i) => (
            <div
              key={t.id}
              className="h-[2px] rounded-full transition-all duration-700"
              style={{
                width: i === tickIndex ? 32 : 16,
                background: i === tickIndex ? BRAND.primary : 'rgba(255,255,255,0.15)',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Beat caption ────────────────────────────────────────────────────────────

const CAPTIONS: Record<number, string> = {
  1: 'It starts with a pleading.',
  2: 'Then the full case file.',
  3: 'JuryView understands what\u2019s at stake.',
  4: 'A venue-calibrated instrument forms.',
  5: 'Synthetic jurors deliberate.',
  6: 'Findings that sharpen the case.',
  7: 'Ask one question. Get a real answer.',
  8: 'Or any question — through the venue\u2019s eyes.',
  9: '',  // Closing slide is the message — no caption underneath
};

const BeatCaption: React.FC<{ beatId: number }> = ({ beatId }) => {
  const caption = CAPTIONS[beatId];
  // If a beat has no caption (e.g. Beat 9 closing slide is the message
  // itself), don't reserve vertical space for an empty strip.
  if (!caption) return null;
  return (
    <div className="h-6 mt-6 flex items-center justify-center">
      <AnimatePresence mode="wait">
        <motion.p
          key={beatId}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="text-slate-400 text-sm font-medium tracking-wide"
        >
          {caption}
        </motion.p>
      </AnimatePresence>
    </div>
  );
};

// ── Shared scene wrapper ────────────────────────────────────────────────────

interface SceneProps {
  reduced: boolean;
}

// Scene entrance transitions:
// - opacity uses the standard expo-out curve for the entrance/exit
// - scale uses a slow LINEAR drift from 0.99 → 1.005 over 3.5s, creating
//   a subtle "camera lean-in" effect across each beat. Total scale change
//   is ~1.5%, well below conscious perception per frame, but the cumulative
//   effect across the beat duration makes the entire reel feel like film
//   instead of a slideshow. After 3.5s the scale holds at 1.005 for the
//   remainder of the beat.
const sceneEnter = {
  initial: { opacity: 0, scale: 0.99 },
  animate: {
    opacity: 1,
    scale: 1.005,
    transition: {
      opacity: { duration: 0.9, ease: EASE },
      scale: { duration: 3.5, ease: 'linear' as const },
    },
  },
  exit: { opacity: 0, transition: { duration: 0.7, ease: EASE_IN } },
};

// ── BEAT 1 — Drop a complaint ───────────────────────────────────────────────

const Beat1Drop: React.FC<SceneProps> = ({ reduced }) => {
  return (
    <motion.div
      {...sceneEnter}
      className="absolute inset-0 flex items-center justify-center"
    >
      <div className="relative flex flex-col items-center">
        {/* Upload zone — dashed border that softens to solid blue */}
        <motion.div
          className="relative w-[320px] h-[200px] rounded-2xl border-2 border-dashed flex items-center justify-center"
          initial={{ borderColor: 'rgba(148, 163, 184, 0.4)' }}
          animate={{ borderColor: BRAND.primary }}
          transition={{ duration: 0.7, delay: 1.3, ease: EASE }}
        >
          {/* Document tile glides in from below */}
          <motion.div
            initial={reduced ? { opacity: 0 } : { y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4, ease: EASE }}
            className="relative"
          >
            <div className={`${cardClass} w-[180px] px-5 py-4 flex items-center gap-3 overflow-hidden`}>
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: BRAND.primaryFaint }}
              >
                <FileText className="w-5 h-5" style={{ color: BRAND.primary }} />
              </div>
              <div className="min-w-0">
                <p className={headerClass}>Complaint.pdf</p>
                <p className="text-[10px] text-slate-500 mt-0.5">3 pages · 142 KB</p>
              </div>

              {/* Shimmer sweep */}
              <motion.div
                className="absolute inset-0 pointer-events-none"
                initial={{ x: '-100%' }}
                animate={{ x: '100%' }}
                transition={{ duration: 0.8, delay: 1.0, ease: EASE }}
                style={{
                  background: `linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%)`,
                }}
              />
            </div>
          </motion.div>
        </motion.div>

        {/* Helper text fades */}
        <motion.p
          initial={{ opacity: 1 }}
          animate={{ opacity: 0 }}
          transition={{ duration: 0.5, delay: 1.3, ease: EASE }}
          className="text-slate-500 text-xs mt-6 font-medium tracking-wide"
        >
          Drop a pleading to begin
        </motion.p>
      </div>
    </motion.div>
  );
};

// ── BEAT 2 — Bundle intake (multiple case files) ────────────────────────────

interface BundleFile {
  name: string;
  meta: string;
  appearAt: number;  // when the row appears
  doneAt: number;    // when the spinner becomes a check
}

const BUNDLE_FILES: BundleFile[] = [
  { name: 'Complaint.pdf',          meta: '3 pp · 142 KB',   appearAt: 0.3, doneAt: 1.0 },
  { name: 'Answer.pdf',             meta: '5 pp · 198 KB',   appearAt: 0.6, doneAt: 1.3 },
  { name: 'Discovery_Memo.pdf',     meta: '12 pp · 410 KB',  appearAt: 0.9, doneAt: 1.7 },
  { name: 'Witness_List.pdf',       meta: '2 pp · 84 KB',    appearAt: 1.2, doneAt: 2.0 },
  { name: 'Insurance_Corr.pdf',     meta: '8 pp · 256 KB',   appearAt: 1.5, doneAt: 2.4 },
  { name: 'Maintenance_Logs.pdf',   meta: '4 pp · 118 KB',   appearAt: 1.8, doneAt: 2.7 },
];

const Beat2BundleIntake: React.FC<SceneProps> = ({ reduced }) => {
  const totalFiles = BUNDLE_FILES.length;

  return (
    <motion.div
      {...sceneEnter}
      className="absolute inset-0 flex items-center justify-center"
    >
      <div className={`${cardClass} w-[440px] px-6 py-5 relative`}>
        <div className="flex items-center justify-between">
          <div>
            <p className={labelClass}>Intake</p>
            <h2 className="text-[13px] font-bold text-slate-900 mt-1">Case File Bundle</h2>
          </div>
          {/* Live counter */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2, ease: EASE }}
            className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-md"
            style={{ background: BRAND.primaryFaint }}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.4, delay: 2.7, ease: EASE }}
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: BRAND.primary }}
            />
            <span className="text-[10px] font-bold tracking-wider" style={{ color: BRAND.primaryDeep }}>
              {totalFiles}/{totalFiles}
            </span>
          </motion.div>
        </div>

        {/* File rows — tightened spacing for vertical compactness */}
        <div className="mt-3 space-y-1">
          {BUNDLE_FILES.map((file, i) => (
            <BundleRow key={file.name} file={file} index={i} reduced={reduced} />
          ))}
        </div>

        {/* Footer — total pages read */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 2.9, ease: EASE }}
          className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between"
        >
          <span className="text-[10.5px] text-slate-500 font-medium">
            Reading 34 pages · Building case understanding
          </span>
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 3.0, ease: EASE }}
            className="w-4 h-4 rounded-full flex items-center justify-center"
            style={{ background: BRAND.primary }}
          >
            <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  );
};

const BundleRow: React.FC<{ file: BundleFile; index: number; reduced: boolean }> = ({ file, reduced }) => {
  return (
    <motion.div
      initial={reduced ? { opacity: 0 } : { opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.45, delay: file.appearAt, ease: EASE }}
      // Polished file row: stronger 1px slate-300 border + layered shadow stack
      // (drop + inset highlight) so each row reads as its own physical surface.
      // Subtle white→cream gradient adds depth without being washed out.
      className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-md border border-slate-300"
      style={{
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
        boxShadow:
          '0 1px 2px rgba(15, 23, 42, 0.08), 0 1px 1px rgba(15, 23, 42, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.9)',
      }}
    >
      {/* File icon — bold border + filled gold tint for the icon glyph */}
      <div
        className="w-5 h-5 rounded flex items-center justify-center shrink-0 border border-slate-400"
        style={{
          background: BRAND.primaryFaint,
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)',
        }}
      >
        <FileText className="w-2.5 h-2.5" style={{ color: BRAND.primaryDeep }} strokeWidth={2.5} />
      </div>
      {/* Filename + meta — heavier weight, deeper text color for definition */}
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-bold text-slate-900 truncate leading-tight">{file.name}</p>
        <p className="text-[9px] text-slate-600 mt-px font-medium">{file.meta}</p>
      </div>
      {/* Status indicator: spinner → check */}
      <BundleStatus appearAt={file.appearAt} doneAt={file.doneAt} reduced={reduced} />
    </motion.div>
  );
};

const BundleStatus: React.FC<{ appearAt: number; doneAt: number; reduced: boolean }> = ({ appearAt, doneAt, reduced }) => {
  return (
    <div className="relative w-4 h-4 shrink-0">
      {/* Spinner ring (visible from appearAt → doneAt) */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 1, 0] }}
        transition={{
          duration: doneAt - appearAt + 0.1,
          delay: appearAt,
          times: [0, 0.05, 0.95, 1],
          ease: 'linear',
        }}
        className="absolute inset-0 rounded-full border-2 border-slate-200"
        style={{ borderTopColor: BRAND.primary }}
      >
        <motion.div
          animate={reduced ? {} : { rotate: 360 }}
          transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
          className="w-full h-full rounded-full border-2 border-transparent"
          style={{ borderTopColor: BRAND.primary }}
        />
      </motion.div>
      {/* Check icon (appears at doneAt) */}
      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35, delay: doneAt, ease: EASE }}
        className="absolute inset-0 rounded-full flex items-center justify-center"
        style={{ background: BRAND.primary }}
      >
        <Check className="w-2.5 h-2.5 text-white" strokeWidth={4} />
      </motion.div>
    </div>
  );
};

// ── BEAT 3 — Read + Recommend ───────────────────────────────────────────────
// The most important narrative beat. Two-stage reveal:
// Stage A (0.0s → 2.0s): system shows it understood the case
// Stage B (2.2s → 4.0s): system shows what it recommends testing
// This is the beat that says "the intelligence layer doesn't just ingest —
// it reasons about what matters, then tells you where to focus."

const Beat3ReadAndRecommend: React.FC<SceneProps> = ({ reduced }) => {
  const understanding = [
    { label: 'Premises Liability', accent: 'slate' },
    { label: 'Defense Side', accent: 'primary' },
    { label: 'County Venue · Single Plaintiff', accent: 'plain' },
  ];

  const recommendations = [
    { label: 'Damages range', priority: 'high' },
    { label: 'Liability framing', priority: 'high' },
    { label: 'Witness credibility', priority: 'medium' },
  ];

  return (
    <motion.div
      {...sceneEnter}
      className="absolute inset-0 flex items-center justify-center"
    >
      <div className={`${cardClass} w-[460px] px-8 py-7 relative`}>
        {/* ── Stage A: Read ── */}
        <p className={labelClass}>Read</p>
        <motion.h2
          initial={reduced ? { opacity: 0 } : { opacity: 0, filter: 'blur(4px)' }}
          animate={{ opacity: 1, filter: 'blur(0px)' }}
          transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
          className="text-base font-bold text-slate-900 mt-1.5"
        >
          I see what's at stake.
        </motion.h2>

        <div className="mt-3 flex flex-wrap gap-2">
          {understanding.map((line, i) => (
            <motion.div
              key={line.label}
              initial={reduced ? { opacity: 0 } : { opacity: 0, filter: 'blur(4px)', y: 4 }}
              animate={{ opacity: 1, filter: 'blur(0px)', y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 + i * 0.25, ease: EASE }}
            >
              {line.accent === 'primary' && (
                <span
                  className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold"
                  style={{ background: BRAND.primaryFaint, color: BRAND.primaryDeep }}
                >
                  {line.label}
                </span>
              )}
              {line.accent === 'slate' && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-700">
                  {line.label}
                </span>
              )}
              {line.accent === 'plain' && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-slate-50 text-slate-600 border border-slate-200">
                  {line.label}
                </span>
              )}
            </motion.div>
          ))}
        </div>

        {/* ── Divider ── */}
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 0.7, delay: 1.9, ease: EASE }}
          style={{ transformOrigin: 'left' }}
          className="mt-5 mb-4 h-px bg-slate-200"
        />

        {/* ── Stage B: Recommend ── */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 2.0, ease: EASE }}
        >
          <p className={labelClass}>Recommend</p>
        </motion.div>
        <motion.h3
          initial={reduced ? { opacity: 0 } : { opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 2.2, ease: EASE }}
          className="text-[13px] font-bold text-slate-900 mt-1.5"
        >
          Where to focus the test
        </motion.h3>

        <div className="mt-3 space-y-2">
          {recommendations.map((rec, i) => (
            <motion.div
              key={rec.label}
              initial={reduced ? { opacity: 0 } : { opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.55, delay: 2.6 + i * 0.22, ease: EASE }}
              className="flex items-center gap-3"
            >
              {/* Priority indicator */}
              <div className="flex items-center gap-0.5 shrink-0">
                <div
                  className="w-1 h-3 rounded-full"
                  style={{ background: BRAND.primary }}
                />
                <div
                  className="w-1 h-3 rounded-full"
                  style={{ background: rec.priority === 'high' ? BRAND.primary : BRAND.slate300 }}
                />
                <div
                  className="w-1 h-3 rounded-full"
                  style={{ background: rec.priority === 'high' ? BRAND.primary : BRAND.slate300 }}
                />
              </div>
              {/* Recommendation label */}
              <span
                className="px-3 py-1.5 rounded-md text-[11.5px] font-semibold border"
                style={{
                  background: BRAND.primaryFaint,
                  borderColor: BRAND.primarySoft,
                  color: BRAND.primaryDeep,
                }}
              >
                {rec.label}
              </span>
            </motion.div>
          ))}
        </div>

        {/* Footer — methodology grounding */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 3.6, ease: EASE }}
          className="mt-5 pt-4 border-t border-slate-100 flex items-center gap-2"
        >
          <Target className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-[11px] text-slate-500 font-medium">
            Test design adapts to your case
          </span>
        </motion.div>
      </div>
    </motion.div>
  );
};

// ── BEAT 4 — Research design ────────────────────────────────────────────────

const Beat4Design: React.FC<SceneProps> = ({ reduced }) => {
  const phases = [
    { icon: ClipboardList, label: 'Baseline', dots: 6 },
    { icon: MessageSquare, label: 'Plaintiff frame', dots: 4 },
    { icon: Scale, label: 'Defense frame', dots: 4 },
    { icon: Users, label: 'Damages anchor', dots: 3 },
    { icon: RotateCcw, label: 'Reflection', dots: 2 },
  ];

  return (
    <motion.div
      {...sceneEnter}
      className="absolute inset-0 flex items-center justify-center"
    >
      <div className={`${cardClass} w-[440px] px-7 py-6 relative overflow-hidden`}>
        {/* Vertical progress sweep on left edge */}
        <motion.div
          className="absolute left-0 top-0 bottom-0 w-[2px]"
          style={{
            background: `linear-gradient(to bottom, transparent 0%, ${BRAND.primary} 50%, transparent 100%)`,
          }}
          initial={{ y: '-100%' }}
          animate={{ y: '100%' }}
          transition={{ duration: 2.4, delay: 1.6, ease: 'linear' }}
        />

        <div className="flex items-center justify-between">
          <div>
            <p className={labelClass}>Assessment Design</p>
            <h2 className="text-base font-bold text-slate-900 mt-1.5">5 Phases</h2>
          </div>
          <div className="text-right">
            <p className={labelClass}>Questions</p>
            <p className="text-base font-bold text-slate-900 mt-1.5">19</p>
          </div>
        </div>

        {/* Phase rows — staggered so each phase reads before the next appears */}
        <div className="mt-5 space-y-2">
          {phases.map((phase, i) => {
            const Icon = phase.icon;
            return (
              <motion.div
                key={phase.label}
                initial={reduced ? { opacity: 0 } : { opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.55, delay: 0.7 + i * 0.22, ease: EASE }}
                className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-50 border border-slate-100"
              >
                <div className="w-7 h-7 rounded-md bg-white border border-slate-200 flex items-center justify-center shrink-0">
                  <Icon className="w-3.5 h-3.5 text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-semibold text-slate-800">
                    Phase {i + 1} · {phase.label}
                  </p>
                </div>
                <div className="flex items-center gap-[3px]">
                  {Array.from({ length: phase.dots }).map((_, di) => (
                    <motion.div
                      key={di}
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.35, delay: 1.0 + i * 0.22 + di * 0.05, ease: EASE }}
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: BRAND.primarySoft }}
                    />
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 2.6, ease: EASE }}
          className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between"
        >
          <span className="text-[11px] text-slate-500 font-medium">Ready to deploy to panel</span>
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: BRAND.primary }} />
            <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: BRAND.primaryDeep }}>
              Standing by
            </span>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

// ── BEAT 5 — The panel deliberates ──────────────────────────────────────────

// Deterministic tile group assignment — visual ratio ~60/30/10 but visually
// scattered, never appears as a number on screen.
function tileGroup(i: number): 'a' | 'b' | 'c' {
  // Knuth multiplicative hash → spreads tiles pseudo-randomly
  const hash = (i * 2654435761) >>> 0;
  const bucket = hash % 100;
  if (bucket < 60) return 'a';
  if (bucket < 90) return 'b';
  return 'c';
}

const Beat5Deliberates: React.FC<SceneProps> = ({ reduced }) => {
  const cols = 25;
  const rows = 5;
  const total = cols * rows; // 125
  const tileSize = 14;
  const gap = 4;
  const gridWidth = cols * (tileSize + gap) - gap;
  const gridHeight = rows * (tileSize + gap) - gap;

  const tiles = useMemo(() => Array.from({ length: total }, (_, i) => i), [total]);

  return (
    <motion.div
      {...sceneEnter}
      className="absolute inset-0 flex items-center justify-center"
    >
      <div className={`${cardClass} w-[500px] px-8 py-7 relative`}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className={labelClass}>Synthetic Panel</p>
            <h2 className="text-base font-bold text-slate-900 mt-1.5">125 Participants</h2>
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4, ease: EASE }}
            className="flex items-center gap-1.5"
          >
            <motion.div
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
              className="w-2 h-2 rounded-full"
              style={{ background: BRAND.primary }}
            />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-600">
              Deliberating
            </span>
          </motion.div>
        </div>

        {/* Participant grid — single SVG, CSS animations, performant */}
        <div className="flex justify-center">
          <svg
            width={gridWidth}
            height={gridHeight}
            viewBox={`0 0 ${gridWidth} ${gridHeight}`}
            style={{ display: 'block' }}
          >
            <style>{`
              @keyframes prevailTilePulse {
                0%   { opacity: 0.25; }
                40%  { opacity: 1; }
                100% { opacity: 1; }
              }
              .prevail-tile {
                animation: prevailTilePulse 2.4s cubic-bezier(0.22, 1, 0.36, 1) both;
                ${reduced ? 'animation-duration: 0.6s;' : ''}
              }
            `}</style>
            {tiles.map((i) => {
              const col = i % cols;
              const row = Math.floor(i / cols);
              const x = col * (tileSize + gap);
              const y = row * (tileSize + gap);
              const group = tileGroup(i);
              const fill =
                group === 'a' ? BRAND.primarySoft :
                group === 'b' ? BRAND.slate400 :
                BRAND.slate200;
              // Wave delay based on column position so the wave moves left → right.
              // 80ms per column × 25 columns = 2.0s wave duration — slow enough to feel
              // like a panel deliberating, not a UI loading spinner.
              const delay = reduced ? 0 : col * 0.08;
              return (
                <rect
                  key={i}
                  className="prevail-tile"
                  x={x}
                  y={y}
                  width={tileSize}
                  height={tileSize}
                  rx={2}
                  fill={fill}
                  style={{ animationDelay: `${delay}s` }}
                />
              );
            })}
          </svg>
        </div>

        {/* Stylized split bar — pure visual, no labels.
            Appears after the wave has fully completed across the panel. */}
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 0.9, delay: 2.6, ease: EASE }}
          style={{ transformOrigin: 'left' }}
          className="mt-6 h-[3px] rounded-full overflow-hidden flex"
        >
          <div className="h-full" style={{ background: BRAND.primary, width: '60%' }} />
          <div className="h-full" style={{ background: BRAND.slate400, width: '30%' }} />
          <div className="h-full" style={{ background: BRAND.slate200, width: '10%' }} />
        </motion.div>

        {/* Methodology footnote */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 3.0, ease: EASE }}
          className="mt-3 flex items-center justify-between"
        >
          <span className="text-[10px] text-slate-500 font-medium">
            Modeled on your venue demographics
          </span>
          <span className="text-[10px] text-slate-400 font-medium">
            5 phases · 19 questions
          </span>
        </motion.div>
      </div>
    </motion.div>
  );
};

// ── BEAT 6 — Insight resolves ───────────────────────────────────────────────

const Beat6Resolves: React.FC<SceneProps> = ({ reduced }) => {
  const findings = [
    { width: 70, color: BRAND.primary, hasIcon: true },
    { width: 55, color: BRAND.slate700, hasIcon: false },
    { width: 60, color: BRAND.slate700, hasIcon: false },
  ];

  return (
    <motion.div
      {...sceneEnter}
      className="absolute inset-0 flex items-center justify-center"
    >
      <motion.div
        className={`${cardClass} w-[440px] px-8 py-7 relative`}
        initial={{ boxShadow: '0 25px 50px -12px rgba(0,0,0,0.4)' }}
        animate={{
          boxShadow: [
            '0 25px 50px -12px rgba(0,0,0,0.4)',
            `0 25px 80px -12px ${BRAND.emerald}40`,
            '0 25px 50px -12px rgba(0,0,0,0.4)',
          ],
        }}
        transition={{ duration: 1.4, delay: 1.8, ease: EASE, times: [0, 0.5, 1] }}
      >
        <div className="flex items-center justify-between">
          <p className={labelClass}>Bottom Line</p>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 1.5, ease: EASE }}
            className="px-2.5 py-1 rounded-full text-[10px] font-semibold"
            style={{ background: BRAND.primaryFaint, color: BRAND.primaryDeep }}
          >
            Ready to brief
          </motion.div>
        </div>

        <motion.h2
          initial={reduced ? { opacity: 0 } : { opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3, ease: EASE }}
          className="text-lg font-bold text-slate-900 mt-2"
        >
          Three findings that change the case.
        </motion.h2>

        <div className="mt-6 space-y-4">
          {findings.map((finding, i) => (
            <motion.div
              key={i}
              initial={reduced ? { opacity: 0 } : { opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.55, delay: 0.6 + i * 0.22, ease: EASE }}
              className="flex items-center gap-3"
            >
              <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ background: finding.hasIcon ? BRAND.primaryFaint : BRAND.slate100 }}>
                {finding.hasIcon ? (
                  <Check className="w-3 h-3" style={{ color: BRAND.primary }} strokeWidth={3} />
                ) : (
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                )}
              </div>
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.75, delay: 0.7 + i * 0.22, ease: EASE }}
                style={{ width: `${finding.width}%`, transformOrigin: 'left' }}
                className="h-[6px] rounded-full"
              >
                <div className="w-full h-full rounded-full" style={{ background: finding.color }} />
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Beat 6 closer — short prompt for the user to engage with the data.
            The transformation tagline now lives in Beat 7 (Ask Anything). */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 2.0, ease: EASE }}
          className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between"
        >
          <span className="text-[11px] text-slate-500 font-medium">Ask anything about the panel</span>
          <Sparkles className="w-3.5 h-3.5" style={{ color: BRAND.primary }} />
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

// ── BEAT 7 — Ask anything (closer) ──────────────────────────────────────────
// The user follow-up moment. A chat workspace where the user types a
// universal question about the case results, and the system fetches a
// styled, structured answer. This is the beat that says "and you can
// interrogate the findings — it's not just a one-shot report."

const ASK_QUESTION = 'What did the panel hesitate on?';

interface HesitationFinding {
  label: string;
  width: number;       // 0-100, visual proportion only — no real percentage shown
  emphasis: 'high' | 'med' | 'low';
}

const HESITATION_FINDINGS: HesitationFinding[] = [
  { label: 'Damages framing',     width: 88, emphasis: 'high' },
  { label: 'Witness credibility', width: 62, emphasis: 'med'  },
  { label: 'Causation chain',     width: 38, emphasis: 'low'  },
];

const Beat7AskAnything: React.FC<SceneProps> = ({ reduced }) => {
  return (
    <motion.div
      {...sceneEnter}
      className="absolute inset-0 flex items-center justify-center"
    >
      <div className={`${cardClass} w-[480px] px-7 py-6 relative`}>
        {/* Workspace header */}
        <div className="flex items-center justify-between">
          <div>
            <p className={labelClass}>Workspace</p>
            <h2 className="text-base font-bold text-slate-900 mt-1.5">Ask anything</h2>
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2, ease: EASE }}
            className="flex items-center gap-1.5"
          >
            <Sparkles className="w-3.5 h-3.5" style={{ color: BRAND.primary }} />
            <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: BRAND.primaryDeep }}>
              Live
            </span>
          </motion.div>
        </div>

        {/* Chat thread area — fixed height so layout doesn't jump */}
        <div className="mt-4 min-h-[200px]">
          {/* User message — slides up from below, right-aligned */}
          <motion.div
            initial={reduced ? { opacity: 0 } : { opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.5, ease: EASE }}
            className="flex justify-end mb-3"
          >
            <div
              className="max-w-[78%] px-3.5 py-2 rounded-2xl rounded-br-md text-[12px] font-medium leading-snug"
              style={{
                background: BRAND.primary,
                color: BRAND.white,
                boxShadow: '0 4px 12px rgba(68, 167, 222, 0.25)',
              }}
            >
              {ASK_QUESTION}
            </div>
          </motion.div>

          {/* Typing indicator — visible briefly between user msg and AI response */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 1, 0] }}
            transition={{
              duration: 0.9,
              delay: 1.3,
              times: [0, 0.15, 0.85, 1],
              ease: 'linear',
            }}
            className="flex justify-start mb-3"
          >
            <div className="px-3 py-2 rounded-2xl rounded-bl-md bg-slate-100 flex items-center gap-1">
              <TypingDot delay={0} />
              <TypingDot delay={0.15} />
              <TypingDot delay={0.3} />
            </div>
          </motion.div>

          {/* AI response card — left-aligned, structured findings */}
          <motion.div
            initial={reduced ? { opacity: 0 } : { opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.7, delay: 2.1, ease: EASE }}
            className="flex justify-start"
          >
            <div
              className="w-full max-w-[88%] px-4 py-3.5 rounded-2xl rounded-bl-md bg-slate-50 border border-slate-200"
              style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)' }}
            >
              {/* Response header */}
              <div className="flex items-center gap-2 mb-3">
                <div
                  className="w-5 h-5 rounded-md flex items-center justify-center"
                  style={{ background: BRAND.primaryFaint }}
                >
                  <Sparkles className="w-3 h-3" style={{ color: BRAND.primary }} />
                </div>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 2.5, ease: EASE }}
                  className="text-[12.5px] font-bold text-slate-900"
                >
                  Three points of hesitation
                </motion.p>
              </div>

              {/* Hesitation bars */}
              <div className="space-y-2">
                {HESITATION_FINDINGS.map((finding, i) => (
                  <HesitationRow
                    key={finding.label}
                    finding={finding}
                    delay={2.7 + i * 0.22}
                    reduced={reduced}
                  />
                ))}
              </div>

              {/* Source line */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 3.7, ease: EASE }}
                className="mt-3 pt-2.5 border-t border-slate-200 flex items-center gap-1.5"
              >
                <div className="w-1 h-1 rounded-full bg-slate-400" />
                <span className="text-[10px] text-slate-500 font-medium">
                  Source: 125 panel responses · Phase 4 (damages anchor)
                </span>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Beat 7 footer — sets up Beat 8 with "and there's more to ask" */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 4.0, ease: EASE }}
          className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between"
        >
          <span className="text-[11px] text-slate-500 font-medium">
            That's one question.
          </span>
          <span className="text-[11px] font-bold" style={{ color: BRAND.primaryDeep }}>
            Ask anything →
          </span>
        </motion.div>
      </div>
    </motion.div>
  );
};

const TypingDot: React.FC<{ delay: number }> = ({ delay }) => (
  <motion.div
    animate={{
      opacity: [0.3, 1, 0.3],
      y: [0, -2, 0],
    }}
    transition={{
      duration: 0.9,
      repeat: Infinity,
      delay,
      ease: 'easeInOut',
    }}
    className="w-1.5 h-1.5 rounded-full bg-slate-500"
  />
);

const HesitationRow: React.FC<{ finding: HesitationFinding; delay: number; reduced: boolean }> = ({
  finding, delay, reduced,
}) => {
  const barColor =
    finding.emphasis === 'high' ? BRAND.primary :
    finding.emphasis === 'med'  ? BRAND.primarySoft :
    BRAND.slate400;

  return (
    <motion.div
      initial={reduced ? { opacity: 0 } : { opacity: 0, x: -6 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay, ease: EASE }}
      className="flex items-center gap-3"
    >
      {/* Label */}
      <span className="text-[11px] font-semibold text-slate-700 w-[120px] shrink-0">
        {finding.label}
      </span>
      {/* Bar track */}
      <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden relative">
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: finding.width / 100 }}
          transition={{ duration: 0.85, delay: delay + 0.15, ease: EASE }}
          style={{ transformOrigin: 'left', background: barColor }}
          className="absolute inset-0 rounded-full"
        />
      </div>
    </motion.div>
  );
};

// ── BEAT 8 — Crescendo: breadth of capabilities ─────────────────────────────
// The closer. Beat 7 showed ONE question and ONE styled answer.
// Beat 8 cascades through SIX capability cards spanning what the system
// actually CAPTURES so the viewer realizes: "wait, I can ask about ALL of that?"
//
// IMPORTANT — none of these cards make recommendations or give legal advice.
// Every card frames a measurement the system performs against panel responses:
//   1. Comparative fault → how jurors allocate liability (casePulse.exposureDrivers)
//   2. Voir dire profiles → archetypes that emerge from the panel (voirDire)
//   3. Damages exposure  → where the awards cluster (damagesProjection)
//   4. Theme resonance   → which arguments scored highest (witnessTheme.themeResonance)
//   5. Holdouts          → who's locked into a position (holdoutAnalysis.holdouts)
//   6. Juror voices      → verbatim responses from each panel member (jurorReaction.pullQuotes)
//
// Holdouts replaced Phase movement — more concrete and viscerally meaningful
// to a litigator. "Who can't be moved?" identifies hung-jury risk before
// voir dire, which is a sharper demo moment than tracking opinion shifts
// across deliberation phases.
//
// Settlement framing was removed earlier — Prevail captures juror response
// data, it does not give settlement advice. "Juror voices" replaced it as
// the most viscerally impressive thing the system actually delivers: the
// literal words from real panel members.

interface CapabilityCard {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  question: string;
  tone: 'primary' | 'primarySoft' | 'slate';
  // Visual hint: 3 bar widths showing "this would have data"
  hintBars: [number, number, number];
}

const CAPABILITY_CARDS: CapabilityCard[] = [
  {
    icon: Scale,
    label: 'Comparative fault',
    question: 'How do jurors split?',
    tone: 'primary',
    hintBars: [80, 60, 40],
  },
  {
    icon: Users,
    label: 'Voir dire profiles',
    question: 'Who maps to which archetype?',
    tone: 'primary',
    hintBars: [90, 50, 30],
  },
  {
    icon: TrendingUp,
    label: 'Damages exposure',
    question: "Where do awards cluster?",
    tone: 'primarySoft',
    hintBars: [70, 85, 45],
  },
  {
    icon: MessageSquare,
    label: 'Theme resonance',
    question: 'What moves the panel?',
    tone: 'primarySoft',
    hintBars: [85, 65, 35],
  },
  {
    icon: Lock,
    label: 'Holdouts',
    question: "Who can't be moved?",
    tone: 'slate',
    hintBars: [85, 50, 30],
  },
  {
    icon: Quote,
    label: 'Juror voices',
    question: 'What did each one say?',
    tone: 'slate',
    hintBars: [55, 80, 65],
  },
];

const Beat8Crescendo: React.FC<SceneProps> = ({ reduced }) => {
  // Loop seam: Beat 8 exits noticeably slower than other beats so the
  // transition back to Beat 1 has a longer "hold black" feel. With
  // AnimatePresence mode="wait", Beat 1 cannot mount until Beat 8 has
  // fully exited — extending Beat 8's exit duration creates a natural
  // pause between the closing tagline and the loop restart, framing
  // the reel as a film that respects the viewer rather than a screen
  // that just snaps back to the beginning.
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.99 }}
      animate={{
        opacity: 1,
        scale: 1.005,
        transition: {
          opacity: { duration: 0.9, ease: EASE },
          scale: { duration: 3.5, ease: 'linear' },
        },
      }}
      exit={{
        opacity: 0,
        scale: 1.01,
        transition: {
          opacity: { duration: 1.4, ease: EASE_IN },
          scale: { duration: 1.4, ease: 'linear' },
        },
      }}
      className="absolute inset-0 flex items-center justify-center"
    >
      <div className={`${cardClass} w-[520px] px-7 py-6 relative overflow-hidden`}>
        {/* Subtle ambient sweep across the card during the cascade — implies
            "many things lighting up at once" */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          initial={{ x: '-100%' }}
          animate={{ x: '100%' }}
          transition={{ duration: 2.2, delay: 0.5, ease: EASE }}
          style={{
            background: `linear-gradient(110deg, transparent 0%, ${BRAND.primary}08 50%, transparent 100%)`,
          }}
        />

        {/* Header */}
        <div className="flex items-center justify-between relative">
          <div>
            <p className={labelClass}>Workspace · Live</p>
            <motion.h2
              initial={reduced ? { opacity: 0 } : { opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
              className="text-base font-bold text-slate-900 mt-1.5"
            >
              Ask anything about your case
            </motion.h2>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3, ease: EASE }}
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primarySoft} 100%)`,
              boxShadow: `0 4px 12px ${BRAND.primary}33`,
            }}
          >
            <Sparkles className="w-4 h-4 text-white" />
          </motion.div>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4, ease: EASE }}
          className="text-[11px] text-slate-500 mt-1 font-medium"
        >
          Comparative fault · Voir dire · Damages · Themes · Holdouts · Voices · Witnesses · Phase shifts · Triggers
        </motion.p>

        {/* 3×2 capability grid */}
        <div className="mt-4 grid grid-cols-3 gap-2">
          {CAPABILITY_CARDS.map((card, i) => (
            <CapabilityTile
              key={card.label}
              card={card}
              delay={0.7 + i * 0.22}
              reduced={reduced}
            />
          ))}
        </div>

        {/* Closing tagline — the resolution of the entire reel */}
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 3.2, ease: EASE }}
          className="mt-5 pt-4 border-t border-slate-100"
        >
          <p className="text-[12.5px] text-slate-800 font-bold leading-snug text-center">
            Walk into the courtroom with the venue&rsquo;s mind already mapped.
          </p>
        </motion.div>
      </div>
    </motion.div>
  );
};

const CapabilityTile: React.FC<{ card: CapabilityCard; delay: number; reduced: boolean }> = ({
  card, delay, reduced,
}) => {
  const Icon = card.icon;
  const accentColor =
    card.tone === 'primary'     ? BRAND.primary :
    card.tone === 'primarySoft' ? BRAND.primarySoft :
    BRAND.slate400;
  const accentBg =
    card.tone === 'slate' ? BRAND.slate100 : BRAND.primaryFaint;

  return (
    <motion.div
      initial={reduced ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.55, delay, ease: EASE }}
      className="px-2.5 py-2.5 rounded-lg bg-white border border-slate-200 relative overflow-hidden"
      style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
    >
      {/* Icon */}
      <div
        className="w-6 h-6 rounded-md flex items-center justify-center mb-1.5"
        style={{ background: accentBg }}
      >
        <Icon className="w-3 h-3" style={{ color: accentColor }} />
      </div>

      {/* Label */}
      <p className="text-[10.5px] font-bold text-slate-900 leading-tight">
        {card.label}
      </p>

      {/* Question */}
      <p className="text-[9.5px] text-slate-500 mt-0.5 leading-tight">
        {card.question}
      </p>

      {/* Tiny bar visual hint — implies "this would have data" */}
      <div className="mt-2 flex items-end gap-[2px] h-2">
        {card.hintBars.map((width, bi) => (
          <motion.div
            key={bi}
            initial={{ scaleY: 0 }}
            animate={{ scaleY: width / 100 }}
            transition={{
              duration: 0.6,
              delay: delay + 0.3 + bi * 0.06,
              ease: EASE,
            }}
            className="flex-1 rounded-sm origin-bottom"
            style={{
              background: accentColor,
              opacity: 0.7 + (width / 100) * 0.3,
            }}
          />
        ))}
      </div>
    </motion.div>
  );
};

// ── BEAT 9 — Closing brand slide ────────────────────────────────────────────
// Final beat: JuryView logo + the three value props fading in sequentially.
// This is the resolution of the entire reel — it tells the viewer what the
// product MEANS after they've seen what it DOES.

const Beat9Closing: React.FC<SceneProps> = ({ reduced }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{
        opacity: 1,
        transition: { duration: 1.0, ease: EASE },
      }}
      exit={{
        opacity: 0,
        // Tighter exit (was 1.4s) — Beat 9 already holds for ~5s after the
        // last text fades in, so the loop seam back to Beat 1 doesn't need
        // an extra-long exit hold on top of that.
        transition: { duration: 0.9, ease: EASE_IN },
      }}
      className="absolute inset-0 flex items-center justify-center"
    >
      <div className="flex flex-col items-center max-w-md px-4">
        {/* Brand lockup — horizontal: shield icon in its natural colors
            (matches the site header) paired with the JuryView wordmark in
            white. The wordmark includes the TM superscript, mixed case
            and weight matched to the header logo. */}
        <motion.div
          initial={reduced ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
          className="flex items-center gap-3 mb-8"
        >
          <img
            src="/assets/shield-icon.png"
            alt="JuryView"
            className="h-12 w-auto"
          />
          <span
            className="text-white"
            style={{
              fontSize: '30px',
              fontWeight: 700,
              letterSpacing: '-0.015em',
              lineHeight: 1,
            }}
          >
            JuryView
            <sup
              style={{
                fontSize: '0.42em',
                fontWeight: 600,
                verticalAlign: 'super',
                marginLeft: '2px',
                opacity: 0.85,
              }}
            >
              TM
            </sup>
          </span>
        </motion.div>

        {/* Line 1 — the headline value */}
        <motion.h2
          initial={reduced ? { opacity: 0 } : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
          className="text-white text-2xl font-bold tracking-tight text-center leading-tight"
          style={{ letterSpacing: '-0.02em' }}
        >
          Highest Fidelity to Real Outcomes
        </motion.h2>

        {/* Accent rule under the headline */}
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 0.9, delay: 1.4, ease: EASE }}
          className="mt-5 mb-5 h-px w-16 rounded-full"
          style={{ background: BRAND.primary }}
        />

        {/* Line 2 — supporting */}
        <motion.p
          initial={reduced ? { opacity: 0 } : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.9, ease: EASE }}
          className="text-slate-300 text-base text-center font-medium"
        >
          No more recruitment or participant costs.
        </motion.p>

        {/* Line 3 — supporting */}
        <motion.p
          initial={reduced ? { opacity: 0 } : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 2.6, ease: EASE }}
          className="text-slate-300 text-base text-center font-medium mt-2"
        >
          Work through iterations like never before.
        </motion.p>
      </div>
    </motion.div>
  );
};

export default HeroLoop;
